/**
 * The waiting room from the grown-up's side: what everyone is waiting on, and a
 * yes or a no.
 *
 * Response latency is the metric this product lives or dies on — the way these
 * apps fail is the child asks, the parent doesn't answer, and the child stops
 * believing the number. So the answer is one tap, with the balance and the
 * price already worked out next to it, and nothing has to be opened first.
 *
 * The wait is the one thing a tap cannot skip. It can be *waived*, in the open,
 * with a reason — because a cooldown a parent cannot override is a cooldown
 * that gets switched off, and a cooldown that can be overridden silently is a
 * rule nobody believes. Both failures end the same way; only the visible waiver
 * survives contact with a real family. The monthly toy limit works the same
 * way and for the same reason, on its own field: a toy can be both early and
 * over the limit, and each override carries its own sentence rather than one
 * standing in for the other.
 */

import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.jsx';
import { useFamily } from '../lib/family.jsx';
import { decide, formatDay, useWishes, waive } from '../lib/wishes.js';
import { BUCKET_LABEL, EMPTY_BALANCE, useBank } from '../lib/bank.js';
import { KIND_LABEL } from '../lib/houseRulesDefault.js';
import { isReady, monthKey, savingProgress, toyCapStatus } from '../domain/waiting.js';
import { formatCents } from '../domain/money.js';

/**
 * A required reason, because a waiver nobody can read is not a waiver.
 *
 * Two rules can need waiving on the same wish — the wait and the month's toy
 * limit — so the form is one component with its own label and its own button,
 * and the two are separate pieces of state on the queue. A shared reason field
 * would put one sentence on the record for two different overrides.
 */
function WaiveForm({ label, hint, action, placeholder, onSubmit, busy }) {
  const [reason, setReason] = useState('');
  return (
    <form
      className="stack tight"
      onSubmit={(e) => { e.preventDefault(); onSubmit(reason.trim()); }}
    >
      <label>
        {label}
        <input
          value={reason} maxLength={120} required autoFocus placeholder={placeholder}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      <p className="small muted">{hint}</p>
      <button className="primary" type="submit" disabled={busy || !reason.trim()}>
        {busy ? 'Saving…' : action}
      </button>
    </form>
  );
}

export default function WishQueue() {
  const { family, kids, profile } = useFamily();
  const { user } = useAuth();
  const wishes = useWishes(family?.id);
  const bank = useBank(family?.id);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [waiving, setWaiving] = useState(null);
  // Separate from `waiving` on purpose: a toy can be both early and over the
  // month's limit, and each override needs its own reason on the record.
  const [capWaiving, setCapWaiving] = useState(null);
  const [notes, setNotes] = useState({});
  // One line, above the queue rather than inside a card: the card the answer
  // belonged to is gone the moment the answer lands, and a confirmation that
  // unmounts itself is a confirmation nobody reads.
  const [result, setResult] = useState(null);

  // A child looking at the queue is looking at their own list. Nobody else's
  // wishes are on this screen for them to read.
  if (profile) return <Navigate to={`/wishes/${profile.id}`} replace />;

  const childName = (id) => kids.find((k) => k.id === id)?.display_name ?? 'Someone';

  // Ready first, then by the date the wait ends: the ones that can be answered
  // now are the ones a parent is here to answer.
  const waiting = wishes.wishes
    .filter((w) => w.status === 'waiting')
    .slice()
    .sort((a, b) => {
      const ready = Number(isReady(b, wishes.today)) - Number(isReady(a, wishes.today));
      if (ready) return ready;
      return String(a.readyOn ?? '').localeCompare(String(b.readyOn ?? ''));
    });

  async function run(fn) {
    setBusy(true); setError(null); setResult(null);
    try {
      await fn();
      await wishes.reload();
      await bank.reload();
    } catch (e) {
      setError(`That didn’t save — ${e.message}.`);
      setBusy(false);
      return;
    }
    setBusy(false);
  }

  const answer = (wish, approve, waiver = null) => run(async () => {
    await decide({
      wish,
      approve,
      actorId: user.id,
      note: (notes[wish.id] ?? '').trim() || null,
      today: wishes.today,
      spending: wishes.spending,
      // The whole list, because the toy cap is a month of this child's toys
      // and cannot be read off the one wish being answered.
      wishes: wishes.wishes,
      waiver,
    });
    setNotes((n) => ({ ...n, [wish.id]: '' }));
    setCapWaiving(null);
    setResult(approve
      ? `Paid from ${childName(wish.childId)}’s ${BUCKET_LABEL[wish.bucket] ?? BUCKET_LABEL.spend}`
      : null);
  });

  return (
    <main className="app-shell stack">
      <div className="spread">
        <div className="stack tight">
          <p className="eyebrow">{family.name}</p>
          <h1>Wishes</h1>
        </div>
        <Link to="/wishes/settings"><button className="quiet">Rules</button></Link>
      </div>

      {wishes.error && <p className="notice error">{wishes.error}</p>}
      {error && <p className="notice error" role="alert">{error}</p>}
      {result && <p className="notice info" role="status">{result}</p>}
      {wishes.loading && <p className="muted" role="status">Loading…</p>}

      {!wishes.loading && waiting.length === 0 && (
        <p className="muted">Nobody is waiting on anything.</p>
      )}

      {waiting.map((wish) => {
        const balance = bank.byChild[wish.childId]?.balance ?? EMPTY_BALANCE;
        const bucket = wish.bucket ?? 'spend';
        const has = balance[bucket] ?? 0;
        const after = has - wish.amountCents;
        const ready = isReady(wish, wishes.today);
        const isToy = wish.kind === 'toy';
        const month = monthKey(wishes.today);
        // Two readings of the same rule: what this child has already spent on
        // toys this month, and whether saying yes to this one would pass the
        // limit. The candidate wish is only in the second.
        const toySpent = isToy
          ? toyCapStatus(wishes.wishes, { childId: wish.childId, month, spending: wishes.spending })
          : null;
        const toyStatus = isToy
          ? toyCapStatus(wishes.wishes, {
            childId: wish.childId, month, spending: wishes.spending, wish,
          })
          : null;
        const overCap = Boolean(toyStatus?.overCap);
        // A wish asked for out of Save is one the child had been saving toward.
        // How far they got is the fact a parent wants before answering.
        const progress = bucket === 'save'
          ? savingProgress({ wish, saveBalanceCents: balance.save ?? 0 })
          : null;

        return (
          <div className="card stack tight" key={wish.id}>
            <div className="spread">
              <strong>{wish.title}</strong>
              <span className="money">{formatCents(wish.amountCents)}</span>
            </div>
            <p className="small muted">
              {childName(wish.childId)} · {KIND_LABEL[wish.kind] ?? KIND_LABEL.other} ·{' '}
              {BUCKET_LABEL[bucket] ?? BUCKET_LABEL.spend}{' '}
              <span className="money">{formatCents(has)}</span>
            </p>
            {wish.note && <p className="small muted">{wish.note}</p>}

            {progress && (
              <p className="small muted">
                Saved <span className="money">{formatCents(progress.haveCents)}</span> of{' '}
                <span className="money">{formatCents(progress.targetCents)}</span>
              </p>
            )}

            {toySpent?.capCents != null && (
              <p className="small muted">
                Toys this month: <span className="money">{formatCents(toySpent.spentCents)}</span>{' '}
                of <span className="money">{formatCents(toySpent.capCents)}</span>
              </p>
            )}

            {/*
              A parent can front it, and the screen says what happens if they do
              — plainly, with no colour and no question. Same rule as the Fuel
              gauge going below zero: the fact is the whole of the message.
            */}
            {after < 0 && (
              <p className="small muted">
                Saying yes takes {BUCKET_LABEL[bucket] ?? BUCKET_LABEL.spend} to{' '}
                <span className="money">{formatCents(after)}</span>.
              </p>
            )}

            {wish.waivedAt && (
              <p className="small muted">
                Wait skipped{wish.waiverReason ? ` — ${wish.waiverReason}` : ''}.
              </p>
            )}

            {wish.capWaiverReason && (
              <p className="small muted">
                Toy limit passed — {wish.capWaiverReason}.
              </p>
            )}

            <label>
              A note with your answer (optional)
              <input
                value={notes[wish.id] ?? ''} maxLength={120}
                onChange={(e) => setNotes((n) => ({ ...n, [wish.id]: e.target.value }))}
              />
            </label>

            <div className="row">
              {ready && overCap ? (
                // Not a refusal and not a warning — the yes is still one tap
                // away. What it asks for is a sentence, for the same reason the
                // wait waiver does: a limit that can be passed silently is a
                // limit nobody believes, and one that cannot be passed at all
                // is a limit that gets switched off.
                <button
                  style={{ flex: 1 }}
                  disabled={busy}
                  onClick={() => setCapWaiving((w) => (w === wish.id ? null : wish.id))}
                >
                  {capWaiving === wish.id ? 'Keep the limit' : 'Over the toy limit'}
                </button>
              ) : ready ? (
                <button className="primary" style={{ flex: 1 }} disabled={busy} onClick={() => answer(wish, true)}>
                  Yes
                </button>
              ) : (
                <button style={{ flex: 1 }} disabled title={`Ready on ${formatDay(wish.readyOn)}`}>
                  Ready {formatDay(wish.readyOn)}
                </button>
              )}
              <button style={{ flex: 1 }} disabled={busy} onClick={() => answer(wish, false)}>
                No
              </button>
              {!ready && (
                <button
                  className="quiet"
                  onClick={() => setWaiving((w) => (w === wish.id ? null : wish.id))}
                >
                  {waiving === wish.id ? 'Keep the wait' : 'Skip the wait'}
                </button>
              )}
            </div>

            {waiving === wish.id && (
              <WaiveForm
                busy={busy}
                label="Why the wait is being skipped"
                hint="It stays on the wish, where everyone can see it."
                action="Skip the wait"
                placeholder="Birthday party is Saturday"
                onSubmit={(reason) => run(async () => {
                  await waive({ wish, actorId: user.id, reason, today: wishes.today });
                  setWaiving(null);
                })}
              />
            )}

            {/* Its own form and its own state, so a toy that is both early and
                over the limit can carry both reasons rather than one standing
                in for the other. */}
            {capWaiving === wish.id && (
              <WaiveForm
                busy={busy}
                label="Why this one goes over the toy limit"
                hint="It stays on the wish, where everyone can see it."
                action="Yes, over the limit"
                placeholder="It is the birthday present"
                onSubmit={(reason) => answer(wish, true, reason)}
              />
            )}

          </div>
        );
      })}
    </main>
  );
}
